/******************************************************************************
 *** CashExpenseBookMng ** 운영비 / 특별회계 * 현금지출부
*******************************************************************************/
import React from "react";
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import Util from "@/js/common/Util";
import SelectBox from "@/js/common/ui/input/SelectBox";
import { fldInfsProps } from "@/js/common/ui/GridTable";
import Button from "@/js/common/ui/Button";
import { exportToXlsx } from "@/js/common/ExcelExport";
import * as Atom from "@/js/common/Atom";
import { useRecoilValue } from "recoil";

type CashExpenseBookMngProps = {
    systemCode?: string;
};

const CashExpenseBookMng = ({
    systemCode,
}: CashExpenseBookMngProps) => {

    const userInfo = useRecoilValue(Atom.userInfo);

    const [upOgnzCd   , setUpOgnzCd   ] = React.useState<string>(userInfo.upOgnzCd);
    const [ognzCd     , setOgnzCd     ] = React.useState<string>(userInfo.ognzCd);
    const [acntgYear  , setAcntgYear  ] = React.useState<string>(Util.getNowAcntgYear());
    const [acntgMonth , setAcntgMonth ] = React.useState<string>(String(new Date().getMonth() + 1).padStart(2, "0"));

    const [cashExpenseDataArr, setCashExpenseDataArr] = React.useState<any[]>([]);

    /** 시스템별 제목 */
    function getTitle() {
        if (!systemCode) return "현금지출부";

        const map: any = {
            C1: "복지기금 현금지출부",
            C2: "자립기금 현금지출부",
            C3: "발전기금 현금지출부",
            C4: "희생자부조금 현금지출부",
            C5: "퇴직급여충당금 현금지출부",
            E1: "지부운영비 현금지출부",
            F1: "지부장직무활동비 현금지출부",
            G1: "지부대회비 현금지출부",
        };

        return map[systemCode] || "특별회계기금 현금지출부";
    }

    const pageTitle = getTitle();

    const cashExpenseFldInfs: fldInfsProps[] = [
        { type: "text" , fldNm: "trnsDt"        , width: 100, headAlign: "center", dataAlign: "center", label: "날짜"     },
        { type: "text" , fldNm: "seqNo"         , width: 70 , headAlign: "center", dataAlign: "center", label: "순번"     },
        { type: "text" , fldNm: "bgtSbjct1stNm" , width: 120, headAlign: "center", dataAlign: "center", label: "관"       },
        { type: "text" , fldNm: "bgtSbjct2stNm" , width: 120, headAlign: "center", dataAlign: "center", label: "항"       },
        { type: "text" , fldNm: "bgtSbjct3stNm" , width: 150, headAlign: "center", dataAlign: "center", label: "목"       },
        { type: "text" , fldNm: "abstrCn"       , width: 240, headAlign: "center", dataAlign: "left"  , label: "적요"     },
        { type: "comma", fldNm: "incomeAmt"     , width: 110, headAlign: "center", dataAlign: "right" , label: "수입"     },
        { type: "comma", fldNm: "expenseAmt"    , width: 110, headAlign: "center", dataAlign: "right" , label: "지출"     },
        { type: "text" , fldNm: "docNo"         , width: 140, headAlign: "center", dataAlign: "center", label: "문서번호" },
    ];

    const monthNum = Number(acntgMonth);
    const displayYear = (monthNum === 1 || monthNum === 2) ? String(Number(acntgYear) + 1) : acntgYear;
    const displayMonth = acntgMonth ? String(monthNum) : "전체";

    React.useEffect(() => {
        AppContext.showSpinner();

        Axios.dataAccess({
            url: "system/bgt/mbsds/cmptn/select-cash-expense-book-list.do",
            methodType: "post",
            paramType: "json",
            paramData: {
                systemCode : systemCode,
                upOrgCd    : upOgnzCd,
                orgCd      : ognzCd,
                acntgYear  : acntgYear,
                acntgMonth : acntgMonth,
            },
            onSuccessFn: (res: any) => {
                setCashExpenseDataArr(res.result.list || []);
                AppContext.hideSpinner();
            },
        });
    }, [systemCode, upOgnzCd, ognzCd, acntgYear, acntgMonth]);

    function makeCashExpenseRows(list: any[]) {
        const result: any[] = [];

        let currentKey = "";
        let subIncomeAmt = 0;
        let subExpenseAmt = 0;

        let totalIncomeAmt = 0;
        let totalExpenseAmt = 0;

        list.forEach((item: any, idx: number) => {
            const groupKey = `${item.bgtSbjctSeCd}|${item.bgtSbjct1stCd}|${item.bgtSbjct2stCd}|${item.bgtSbjct3stCd}`;

            if (currentKey !== "" && currentKey !== groupKey) {
                result.push({
                    abstrCn: "소계",
                    incomeAmt: subIncomeAmt,
                    expenseAmt: subExpenseAmt,
                });
                subIncomeAmt = 0;
                subExpenseAmt = 0;
            }

            const incomeAmt = Number(item.incomeAmt || 0);
            const expenseAmt = Number(item.expenseAmt || 0);

            result.push({
                trnsDt        : Util.cvtDateStrForm(String(item.trnsDt), "-", "YYYYMMDD"),
                seqNo         : item.seqNo,
                bgtSbjct1stNm : item.bgtSbjct1stNm,
                bgtSbjct2stNm : item.bgtSbjct2stNm,
                bgtSbjct3stNm : item.bgtSbjct3stNm,
                abstrCn       : item.abstrCn,
                incomeAmt,
                expenseAmt,
                docNo         : item.docNo,
            });

            subIncomeAmt += incomeAmt;
            subExpenseAmt += expenseAmt;
            totalIncomeAmt += incomeAmt;
            totalExpenseAmt += expenseAmt;
            currentKey = groupKey;

            if (idx === list.length - 1) {
                result.push({
                    abstrCn: "소계",
                    incomeAmt: subIncomeAmt,
                    expenseAmt: subExpenseAmt,
                });
            }
        });

        if (result.length === 0) return [];

        result.push({
            trnsDt: "합계",
            incomeAmt: totalIncomeAmt,
            expenseAmt: totalExpenseAmt,
        });

        return result;
    }

    function downloadCashExpenseExcel() {
        if (cashExpenseDataArr.length === 0) {
            AppContext.openAlert({
                title: "데이터 확인",
                msg: "엑셀로 다운로드할 데이터가 없습니다.",
                icon: "warning",
            });
            return;
        }

        exportToXlsx({
            fileName: `${displayYear}년 ${acntgMonth ? `${displayMonth}월 ` : ""}${pageTitle}`,
            title: `${displayYear}년 ${acntgMonth ? `${displayMonth}월 ` : ""}${pageTitle}`,
            subTitleLeft: `${acntgYear}회계년도`,
            subTitleRight: "(금액단위:원)",
            columns: cashExpenseFldInfs,
            rows: makeCashExpenseRows(cashExpenseDataArr),
        });
    }

    return (
        <div className="w1200 bgcWhite p20 bdrd8 mt10 boxShadowSoft">
            {/* 타이틀 */}
            <div className="mt5">
                <span className="dpBlock w100p fm-c fs25 fc2c3e50 ls1 pb10">
                    {pageTitle}
                </span>
                <div className="bdt3 bdc007bff pb10" />
            </div>

            {/* 검색 영역 */}
            <div className="bd1 bdcDDDDDD p20 mb10 bdrd6 bgcFAFAFA">
                <div className="dpFlx aiC flxWrap gap14">
                    <div className="dpFlx aiC">
                        <span className="w50 fm-c fs15 fc333333 ls1 taL">본부</span>
                        <SelectBox
                            value={upOgnzCd}
                            width={150}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            background="#F6F6F6"
                            codeArray={Util.getUpOrgCodeArray("01")}
                            placeholder="전체"
                            paddingRight={8}
                            onChangeFunc={(data: string) => setUpOgnzCd(data)}
                            disabled={true}
                        />
                    </div>

                    <div className="dpFlx aiC">
                        <span className="w50 fm-c fs15 fc333333 ls1 taL">지부</span>
                        <SelectBox
                            value={ognzCd}
                            width={150}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            background="#F6F6F6"
                            codeArray={Util.getDownOrgCodeArray(upOgnzCd)}
                            placeholder="전체"
                            paddingRight={8}
                            onChangeFunc={(data: string) => setOgnzCd(data)}
                            disabled={true}
                        />
                    </div>

                    <div className="dpFlx aiC">
                        <span className="w80 fm-c fs15 fc333333 ls1 taL">회계연도</span>
                        <SelectBox
                            value={acntgYear}
                            width={120}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            background="#fff"
                            codeArray={Util.getAcntgYearArray()}
                            placeholder="선택"
                            paddingRight={8}
                            onChangeFunc={(data: string) => setAcntgYear(data)}
                        />
                    </div>

                    <div className="dpFlx aiC">
                        <span className="w80 fm-c fs15 fc333333 ls1 taL">월별 조회</span>
                        <SelectBox
                            value={acntgMonth}
                            width={140}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            background="#fff"
                            paddingRight={8}
                            codeArray={[{ code: "", name: "전체" }].concat(Util.getCodeArray("SRCH_MM"))}
                            placeholder="전체"
                            onChangeFunc={(data: string) => setAcntgMonth(data)}
                        />
                    </div>

                    <div className="mlA">
                        <Button
                            buttonName="현금지출부 Excel"
                            width={150}
                            height={36}
                            fontFamily="fm-b"
                            fontSize={14}
                            letterSpacing={-1}
                            variant="styExcelBtn"
                            borderRadius={5}
                            buttonClass="dpFlx aiC jcC"
                            onClickFunc={() => downloadCashExpenseExcel()}
                        />
                    </div>
                </div>
            </div>

            {/* 리스트 */}
            <div className="w100p bsBdb bgcFDFCFC">
                <div
                    className="w100p"
                    style={{ borderTop: "1px solid #C5C5C5", position: "relative", zIndex: 0 }}
                >
                    <table className="w100p tlF tbl-collapse" style={{ tableLayout: "fixed" }}>
                        <colgroup>
                            <col style={{ width: 100 }} />
                            <col style={{ width: 70 }} />
                            <col style={{ width: 80 }} />
                            <col style={{ width: 110 }} />
                            <col style={{ width: 130 }} />
                            <col style={{ width: 220 }} />
                            <col style={{ width: 130 }} />
                            <col style={{ width: 130 }} />
                            <col style={{ width: 150 }} />
                        </colgroup>

                        <thead>
                            <tr>
                                {["날짜", "순번", "관", "항", "목", "적요", "수입", "지출", "문서번호"].map((label) => (
                                    <th key={label} className="p0 bd1 bdcEDEDED">
                                        <div className="h40 dpFlx aiC jcC pw12 bgcF1F8FF">
                                            <div className="w100p fm-c fs13 fc34495e wsNw ofH toE taC">
                                                {label}
                                            </div>
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {makeCashExpenseRows(cashExpenseDataArr).length === 0 ? (
                                <tr>
                                    <td colSpan={9} className="bd1 bdcEDEDED p16 taC fm-Pretendard-Regular fs13 fc999999">
                                        조회된 데이터가 없습니다.
                                    </td>
                                </tr>
                            ) : (
                                makeCashExpenseRows(cashExpenseDataArr).map((row: any, idx: number) => {
                                    const isTotalRow = row.trnsDt === "합계";
                                    const isSubTotalRow = row.abstrCn === "소계";

                                    const trClassName =
                                        isTotalRow || isSubTotalRow ? "" : "bgcF9F9F9-hover transitionBgc";

                                    const textClassName = isTotalRow
                                        ? "fm-c fs12 fc111111"
                                        : isSubTotalRow
                                            ? "fm-c fs12 fc111111"
                                            : "fm-c fs12 fc666666";

                                    const bgClassName = isTotalRow
                                        ? "bgcF3E7A1"
                                        : isSubTotalRow
                                            ? "bgcDCDCDC"
                                            : "";

                                    return (
                                        <tr key={idx} className={trClassName}>
                                            <td className="bd1 bdcEDEDED p0">
                                                <div className={`h35 dpFlx aiC jcC pw12 ${bgClassName}`}>
                                                    <div className={`w100p wsNw ofH toE taC ${textClassName}`}>
                                                        {row.trnsDt}
                                                    </div>
                                                </div>
                                            </td>

                                            <td className="bd1 bdcEDEDED p0">
                                                <div className={`h35 dpFlx aiC jcC pw12 ${bgClassName}`}>
                                                    <div className={`w100p wsNw ofH toE taC ${textClassName}`}>
                                                        {row.seqNo}
                                                    </div>
                                                </div>
                                            </td>

                                            <td className="bd1 bdcEDEDED p0">
                                                <div className={`h35 dpFlx aiC pw12 ${bgClassName}`}>
                                                    <div className={`w100p wsNw ofH toE ${textClassName}`}>
                                                        {row.bgtSbjct1stNm}
                                                    </div>
                                                </div>
                                            </td>

                                            <td className="bd1 bdcEDEDED p0">
                                                <div className={`h35 dpFlx aiC pw12 ${bgClassName}`}>
                                                    <div className={`w100p wsNw ofH toE ${textClassName}`}>
                                                        {row.bgtSbjct2stNm}
                                                    </div>
                                                </div>
                                            </td>

                                            <td className="bd1 bdcEDEDED p0">
                                                <div className={`h35 dpFlx aiC pw12 ${bgClassName}`}>
                                                    <div className={`w100p wsNw ofH toE ${textClassName}`}>
                                                        {row.bgtSbjct3stNm}
                                                    </div>
                                                </div>
                                            </td>

                                            <td className="bd1 bdcEDEDED p0">
                                                <div className={`h35 dpFlx aiC pw12 ${bgClassName}`}>
                                                    <div className={`w100p wsNw ofH toE ${textClassName}`}>
                                                        {row.abstrCn}
                                                    </div>
                                                </div>
                                            </td>

                                            <td className="bd1 bdcEDEDED p0">
                                                <div className={`h35 dpFlx aiC jcE pw12 ${bgClassName}`}>
                                                    <div className={`w100p wsNw ofH toE taR ${textClassName}`}>
                                                        {Util.comma(String(row.incomeAmt))}
                                                    </div>
                                                </div>
                                            </td>

                                            <td className="bd1 bdcEDEDED p0">
                                                <div className={`h35 dpFlx aiC jcE pw12 ${bgClassName}`}>
                                                    <div className={`w100p wsNw ofH toE taR ${textClassName}`}>
                                                        {Util.comma(String(row.expenseAmt))}
                                                    </div>
                                                </div>
                                            </td>

                                            <td className="bd1 bdcEDEDED p0">
                                                <div className={`h35 dpFlx aiC jcL pw12 ${bgClassName}`}>
                                                    <div className={`w100p wsNw ofH toE ${textClassName}`}>
                                                        {row.docNo}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default CashExpenseBookMng;